import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { featureFlagsApi } from '../services/api';
import { 
  PageHeader, 
  SearchBar, 
  LoadingSpinner, 
  EmptyState, 
  ListContainer, 
  Pagination,
  Badge,
  ErrorMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  FormSelect,
  Card,
  CardHeader,
  CardBody
} from '../components/common';
import { Flag, ArrowLeft, Users, Building, User, ToggleRight, ToggleLeft, Trash2 } from 'lucide-react';

const FeatureTypeDetail = () => {
  const { featureType } = useParams();
  const navigate = useNavigate();
  const [featureFlags, setFeatureFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  const [filterType, setFilterType] = useState('all'); // all, users, buildings
  const [statusFilter, setStatusFilter] = useState(''); // '', true, false
  const [stats, setStats] = useState(null);
  const [toggleLoading, setToggleLoading] = useState({});

  useEffect(() => {
    fetchFeatureFlags();
    fetchStats();
  }, [featureType, currentPage, searchString, filterType, statusFilter]);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const params = {
        featureType: parseInt(featureType),
        pageNumber: currentPage,
        pageSize: pageSize,
        searchString: searchString || undefined,
        isEnabled: statusFilter !== '' ? statusFilter === 'true' : undefined
      };

      // Add building filter if needed
      if (filterType === 'buildings') {
        // This will need to be implemented based on your API - you might need to filter client-side
        // or add a buildingId filter parameter to the API
      }

      const response = await featureFlagsApi.list(params);
      setFeatureFlags(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch feature flags');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await featureFlagsApi.list({
        featureType: parseInt(featureType),
        pageSize: 1000 // Get all for stats
      });
      
      const flags = response.data.items || [];
      const enabledFlags = flags.filter(flag => flag.isEnabled);
      const disabledFlags = flags.filter(flag => !flag.isEnabled);
      const uniqueUsers = new Set(flags.map(flag => flag.userId)).size;
      const uniqueBuildings = new Set(flags.filter(flag => flag.buildingId).map(flag => flag.buildingId)).size;
      const flagsWithBuildings = flags.filter(flag => flag.buildingId).length;
      const globalFlags = flags.filter(flag => !flag.buildingId).length;

      setStats({
        totalFlags: flags.length,
        enabledFlags: enabledFlags.length,
        disabledFlags: disabledFlags.length,
        uniqueUsers,
        uniqueBuildings,
        flagsWithBuildings,
        globalFlags
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleSearch = (value) => {
    setSearchString(value);
    setCurrentPage(1);
  };

  const handleToggleFeature = async (flagId, currentStatus) => {
    try {
      setToggleLoading(prev => ({ ...prev, [flagId]: true }));
      
      const flag = featureFlags.find(f => f.userFeatureFlagId === flagId);
      if (!flag) return;

      const toggleData = {
        userId: flag.userId,
        featureType: flag.featureType,
        isEnabled: !currentStatus
      };
      
      // Only include buildingId if it exists
      if (flag.buildingId) {
        toggleData.buildingId = flag.buildingId;
      }

      await featureFlagsApi.toggle(toggleData);
      await fetchFeatureFlags();
      await fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to toggle feature flag');
    } finally {
      setToggleLoading(prev => ({ ...prev, [flagId]: false }));
    }
  };

  const handleDeleteFlag = async (flagId) => {
    try {
      await featureFlagsApi.delete(flagId);
      await fetchFeatureFlags();
      await fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to delete feature flag');
    }
  };

  const getFeatureTypeName = (featureTypeValue) => {
    // Try to get the name from the first flag in the list, otherwise use default
    const firstFlag = featureFlags.length > 0 ? featureFlags[0] : null;
    return firstFlag?.featureTypeName || `Feature Type ${featureTypeValue}`;
  };

  const getBadgeVariant = (isEnabled) => {
    return isEnabled ? 'success' : 'secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Group flags by type for better display
  const groupedFlags = featureFlags.reduce((acc, flag) => {
    if (flag.buildingId) {
      if (!acc.buildings) acc.buildings = [];
      acc.buildings.push(flag);
    } else {
      if (!acc.users) acc.users = [];
      acc.users.push(flag);
    }
    return acc;
  }, {});

  if (loading && featureFlags.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={getFeatureTypeName(featureType)}
        subtitle={`Manage ${getFeatureTypeName(featureType)} for users and buildings`}
        icon={Flag}
        action={
          <div className="flex space-x-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/feature-flags/types')}
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Feature Types
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate('/feature-flags/create')}
            >
              Add Feature Flag
            </Button>
          </div>
        }
      />

      {error && <ErrorMessage message={error} />}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardBody>
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <Flag size={20} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.totalFlags}
                  </div>
                  <div className="text-sm text-gray-500">Total Flags</div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <ToggleRight size={20} className="text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.enabledFlags}
                  </div>
                  <div className="text-sm text-gray-500">Enabled</div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <Users size={20} className="text-primary-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.uniqueUsers}
                  </div>
                  <div className="text-sm text-gray-500">Users</div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <Building size={20} className="text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stats.uniqueBuildings}
                  </div>
                  <div className="text-sm text-gray-500">Buildings</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SearchBar
          value={searchString}
          onSearch={handleSearch}
          placeholder="Search users, buildings..."
        />
        
        <FormSelect
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setCurrentPage(1);
          }}
          options={[
            { value: 'all', label: 'All Types' },
            { value: 'users', label: 'Users Only' },
            { value: 'buildings', label: 'Buildings Only' }
          ]}
        />

        <FormSelect
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          options={[
            { value: '', label: 'All Statuses' },
            { value: 'true', label: 'Enabled' },
            { value: 'false', label: 'Disabled' }
          ]}
        />
      </div>

      <ListContainer>
        {featureFlags.length === 0 ? (
          <EmptyState
            title="No feature flags found"
            description={`No feature flags found for ${getFeatureTypeName(featureType)} with current filters.`}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <tr>
                  <TableCell header>Target</TableCell>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Last Updated</TableCell>
                  <TableCell header>Actions</TableCell>
                </tr>
              </TableHeader>
              <TableBody>
                {featureFlags.map((flag) => (
                  <TableRow key={flag.userFeatureFlagId}>
                    <TableCell>
                      <div className="flex items-center">
                        {flag.buildingId ? (
                          <Building size={16} className="mr-2 text-purple-500" />
                        ) : (
                          <User size={16} className="mr-2 text-primary-500" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">
                            {flag.buildingId ? 
                              (flag.buildingName || 'Unknown Building') : 
                              (flag.fullName || flag.userName || 'Unknown User')
                            }
                          </div>
                          <div className="text-sm text-gray-500">
                            {flag.buildingId ? flag.buildingId : (flag.email || flag.userId)}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={flag.buildingId ? 'info' : 'secondary'}>
                        {flag.buildingId ? 'Building' : 'User'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBadgeVariant(flag.isEnabled)}>
                        {flag.isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(flag.createdDate)}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(flag.lastUpdated)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleFeature(flag.userFeatureFlagId, flag.isEnabled)}
                          disabled={toggleLoading[flag.userFeatureFlagId]}
                          className="p-1 text-primary-600 hover:text-primary-800 disabled:opacity-50"
                          title={flag.isEnabled ? 'Disable' : 'Enable'}
                        >
                          {toggleLoading[flag.userFeatureFlagId] ? (
                            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : flag.isEnabled ? (
                            <ToggleRight size={16} />
                          ) : (
                            <ToggleLeft size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteFlag(flag.userFeatureFlagId)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalCount}
              pageSize={pageSize}
            />
          </>
        )}
      </ListContainer>
    </div>
  );
};

export default FeatureTypeDetail;